import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Command,
  CheckSquare,
  ListTodo,
  Bell,
  Calendar,
  User,
  Building,
  FileText,
  Clock,
  ArrowRight,
  Loader2,
  X,
  Flag,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Search result type icons
const TYPE_ICONS = {
  task: CheckSquare,
  todo: ListTodo,
  reminder: Bell,
  meeting: Calendar,
  project: FileText,
  user: User,
};

// Priority colors
const PRIORITY_COLORS = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-slate-100 text-slate-600',
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Handle keyboard shortcut (Cmd+K or Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [open]);

  // Search function
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/search/global?q=${encodeURIComponent(searchQuery)}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        // Fallback: search tasks directly
        const taskResponse = await fetch(
          `${API_URL}/api/task-management/tasks?search=${encodeURIComponent(searchQuery)}&limit=5`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (taskResponse.ok) {
          const taskData = await taskResponse.json();
          const taskResults = (taskData.tasks || []).map(task => ({
            id: task.id,
            type: 'task',
            title: task.title,
            subtitle: task.assigned_to_name ? `Assigned to ${task.assigned_to_name}` : '',
            priority: task.priority,
            status: task.status,
            url: `/task-management/${task.id}`,
            isOverdue: task.is_overdue,
          }));
          setResults(taskResults);
        }
        return;
      }

      const data = await response.json();
      setResults(data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[selectedIndex]) {
        handleSelect(results[selectedIndex]);
      }
    }
  };

  // Handle result selection
  const handleSelect = (result) => {
    setOpen(false);
    if (result.url) {
      navigate(result.url);
    }
  };

  // Quick actions
  const quickActions = [
    { id: 'new-task', label: 'Create new task', icon: CheckSquare, url: '/task-management/new' },
    { id: 'my-tasks', label: 'View my tasks', icon: CheckSquare, url: '/task-management' },
    { id: 'calendar', label: 'Open calendar', icon: Calendar, url: '/unified-calendar' },
    { id: 'reminders', label: 'View reminders', icon: Bell, url: '/reminders' },
  ];

  const displayResults = query.length > 1 ? results : [];
  const showQuickActions = query.length === 0;

  return (
    <>
      {/* Search trigger button in header */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
        data-testid="global-search-trigger"
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">Search...</span>
        <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-mono bg-white border rounded shadow-sm">
          <Command className="h-3 w-3" />K
        </kbd>
      </button>

      {/* Search dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 gap-0 max-w-2xl overflow-hidden" data-testid="global-search-dialog">
          <div className="flex items-center gap-3 px-4 py-3 border-b">
            <Search className="h-5 w-5 text-slate-400" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
              onKeyDown={handleKeyDown}
              placeholder="Search tasks, to-dos, reminders..."
              className="flex-1 border-0 shadow-none focus-visible:ring-0 text-base"
              data-testid="global-search-input"
            />
            {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
            <button 
              onClick={() => setOpen(false)}
              className="p-1 hover:bg-slate-100 rounded"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {/* Quick actions when no query */}
            {showQuickActions && (
              <div className="p-2">
                <p className="px-3 py-1.5 text-xs font-medium text-slate-400 uppercase">Quick Actions</p>
                {quickActions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.id}
                      onClick={() => { setOpen(false); navigate(action.url); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        selectedIndex === index ? 'bg-slate-100' : 'hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-700">{action.label}</span>
                      <ArrowRight className="h-4 w-4 text-slate-300 ml-auto" />
                    </button>
                  );
                })}
              </div>
            )}

            {/* Search results */}
            {displayResults.length > 0 && (
              <div className="p-2">
                <p className="px-3 py-1.5 text-xs font-medium text-slate-400 uppercase">Results</p>
                {displayResults.map((result, index) => {
                  const Icon = TYPE_ICONS[result.type] || FileText;
                  const priorityColor = PRIORITY_COLORS[result.priority] || '';
                  
                  return (
                    <button
                      key={result.id}
                      onClick={() => handleSelect(result)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        selectedIndex === index ? 'bg-slate-100' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`search-result-${result.id}`}
                    >
                      <div className={`p-1.5 rounded ${
                        result.type === 'task' ? 'bg-purple-100' :
                        result.type === 'todo' ? 'bg-indigo-100' :
                        result.type === 'reminder' ? 'bg-amber-100' :
                        'bg-slate-100'
                      }`}>
                        <Icon className={`h-4 w-4 ${
                          result.type === 'task' ? 'text-purple-600' :
                          result.type === 'todo' ? 'text-indigo-600' :
                          result.type === 'reminder' ? 'text-amber-600' :
                          'text-slate-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-900 truncate">
                            {result.title}
                          </span>
                          {result.isOverdue && (
                            <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                              <AlertTriangle className="h-3 w-3 mr-0.5" />
                              Overdue
                            </Badge>
                          )}
                          {result.priority && (
                            <Badge className={`text-[10px] px-1.5 py-0 ${priorityColor}`}>
                              {result.priority}
                            </Badge>
                          )}
                        </div>
                        {result.subtitle && (
                          <p className="text-xs text-slate-500 truncate">{result.subtitle}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {result.type}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}

            {/* No results */}
            {query.length > 1 && !loading && displayResults.length === 0 && (
              <div className="p-8 text-center">
                <Search className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm text-slate-500">No results found for "{query}"</p>
                <p className="text-xs text-slate-400 mt-1">Try searching with different keywords</p>
              </div>
            )}
          </div>

          {/* Footer hint */}
          <div className="px-4 py-2 border-t bg-slate-50 flex items-center justify-between text-xs text-slate-400">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border rounded">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-white border rounded">↓</kbd>
                <span>navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border rounded">↵</kbd>
                <span>select</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border rounded">esc</kbd>
                <span>close</span>
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
